











































































































import jQuery from 'jquery';
import moment from 'moment';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import { Getter } from 'vuex-class';
import { DownloadButton } from '@/components/Global/Downloads/DownloadButton.vue';
import { DownloadIcon } from '@/components/Global/Downloads/DownloadIcon.vue';
import { DownloadProgress } from '@/components/Global/Downloads/DownloadProgress.vue';
import {
	AsyncNotifications,
	NotifyListener,
	NotifyEvent,
	notify,
	DocumentStatusMessageType,
} from '@/services/AsyncNotifications';
import {
	Download,
	DownloadQueue,
	UrlReadyWhen,
	Ready,
	resolveDownloadUrl,
} from '@/services/Downloads';
import Reports, { ClaimantNameOption, PceOption, ReportFormat } from '@/services/Reports';
import ReportsApprovalMmic from '../Modal/Mmic/ReportsApprovalMmic.vue';
import { DownloadableLossReport, LossReport, LossReportTerm } from './ReportTypes';
import { PolicyUi } from '@/common/Policy';
import { User } from '@/common/Users';
import Brand from '@/services/Brand';

@Component({
	components: {
		DownloadButton,
		DownloadIcon,
		DownloadProgress,
	},
})
export default class MmicLossHistoryReportItem extends Vue {
	private Brand = Brand.getBrand();

	@Getter('thirtyDayPolicies', { namespace: 'Policy' }) private policies: PolicyUi[];
	@Getter('coreUser', { namespace: 'Auth' }) private coreUserRecord: User;

	@Prop() private readonly claimantName: ClaimantNameOption;
	@Prop() private readonly index: number;
	@Prop() private readonly pceOption: PceOption;
	@Prop() private readonly report: LossReport;

	private readonly ReportFormat = ReportFormat;

	private selectedTerms: LossReportTerm[] = [];
	private excelDownloadId: string = '';
	private pdfDownloadId: string = '';

	private created(): void {
		jQuery(window.document).on('click.bs.dropdown.data-api', '.keep_it_open', e => {
			e.stopPropagation();
		});
	}

	private get dropdownPlaceholderText(): string {
		if (this.selectedTerms.length === this.report.terms.length) {
			return `Show all policy term years (${this.selectedTerms.length})`;
		} else if (this.selectedTerms.length > 0) {
			let text: string = this.selectedTerms
				.slice(0, 5)
				.map((term: LossReportTerm): string => term.term)
				.join(', ');
			if (this.selectedTerms.length > 5) {
				text += ` +${this.selectedTerms.length - 5} more`;
			}
			return text;
		} else {
			return `Select one or more terms (${this.report.terms.length})`;
		}
	}

	private get dropdownSortedTerms(): LossReportTerm[] {
		// Sort descending by effective_from_date.
		return this.report.terms.sort(
			(left: LossReportTerm, right: LossReportTerm): number =>
				new Date(right.effective_from_date).getTime() -
				new Date(left.effective_from_date).getTime(),
		);
	}

	private selectTerm(event: InputEvent, term: LossReportTerm): void {
		if ((event.target as HTMLInputElement).checked) {
			this.selectedTerms.push(term);
		} else {
			this.selectedTerms = this.selectedTerms.filter(
				(selectedTerm: LossReportTerm): boolean => selectedTerm !== term,
			);
		}
		this.generateDownloadIds();
	}

	private selectAllTerms(event: InputEvent): void {
		if ((event.target as HTMLInputElement).checked) {
			this.selectedTerms = this.report.terms.slice();
		} else {
			this.selectedTerms = [];
		}
		this.generateDownloadIds();
	}

	private get isDownloadButtonEnabled(): boolean {
		return this.selectedTerms.length > 0;
	}

	private generateDownloadIds(): void {
		if (this.selectedTerms.length > 0) {
			const commonDownloadId: string = [
				this.$route.path,
				this.report.policy_no,
				this.selectedTerms
					.map((term: LossReportTerm): string => term.term)
					.sort()
					.join(),
			].join(':');
			this.excelDownloadId = `${commonDownloadId}:${ReportFormat.excel}`;
			this.pdfDownloadId = `${commonDownloadId}:${ReportFormat.pdf}`;
		} else {
			this.excelDownloadId = undefined;
			this.pdfDownloadId = undefined;
		}
	}

	private async resolveReportUrl(format: ReportFormat): Promise<string> {
		// The report ID and insured type appears to be the same for all terms, so take those values
		// from the first term. Seems like they should've been hoisted to the outer report object...
		const reportId: string = this.report.terms[0].report_id;
		const insuredType: string = this.report.terms[0].insured_type;

		const response = await Reports.requestLossHistoryReport(
			reportId,
			this.claimantName,
			this.pceOption,
			insuredType,
			format,
			this.selectedTerms.map((term: LossReportTerm): string => term.term),
			format === ReportFormat.excel ? this.excelDownloadId : this.pdfDownloadId,
		);
		const urlReadyWhen: UrlReadyWhen = {
			ready: response.fetching ? Ready.later : Ready.now,
			url: response.s3_url,
		};
		return resolveDownloadUrl(
			urlReadyWhen,
			DocumentStatusMessageType.lossReport,
			format === ReportFormat.excel ? this.excelDownloadId : this.pdfDownloadId,
		);
	}

	private async downloadReport(format: ReportFormat): Promise<Download> {
		const termsText: string = this.selectedTerms
			.map((term: LossReportTerm): string => term.term)
			.join(', ');

		const label: string = `${this.report.policy_holder_name} Loss History Report for ${termsText} in ${format} Format`;

		return DownloadQueue.singleton.enqueue({
			id: format === ReportFormat.excel ? this.excelDownloadId : this.pdfDownloadId,
			fileNameFromClient: Reports.generateFileName({
				date: moment().format('YYYY-MM-DD'),
				extensionWithoutSeparator: format === ReportFormat.excel ? 'csv' : 'pdf',
				policyNumber: this.report.policy_no,
				subtitle: termsText,
				title: 'Loss History Report',
			}),
			label,
			urlPromise: this.resolveReportUrl(format),
		});
	}

	private organizationLabel(reportItem: LossReport, policyList: PolicyUi[]): string {
		const policyNumber = reportItem.policy_no;
		const userClientId = this.coreUserRecord?.policies?.[this.Brand]?.find(up => up.policy_number === policyNumber)?.client_id;
		//We can't locate the clientId the user is associated with the record, so we return the report policyHolderName.
		if (!userClientId) return reportItem.policy_holder_name;
		const matchingPolicy = policyList.find(p => p.policy_number === policyNumber);
		const userIsPolicyholder = userClientId === matchingPolicy?.policy_holder?.client_id;
		//If the user is the policyholder, we display the policyholder name.
		if (userIsPolicyholder) return matchingPolicy.policy_holder.full_name || reportItem.policy_holder_name;
		//Otherwise try to locate the risk the user is associated with to display their name.
		const matchingInsured = policyList.find(p => p.policy_number === policyNumber)?.risks?.[userClientId]?.insured;
		//Of course default to report policyHolderName if matching insured isn't identified.
		return matchingInsured?.org || matchingInsured?.name || reportItem.policy_holder_name;
	}
}
